import React, { useEffect, useMemo } from 'react';

import SuspiciousUser from 'components/Globals/SuspiciousUser';

import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import { useUserData } from 'utils/hooks/useAuthenticatedUser';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Globals/queries';

import { RENDERING_STATUSES, ROUTE_RESERVED_KEYWORDS, VISITOR_TRUST_SOURCES } from 'operabase-router/constants';

const SuspiciousUserCheck = ({ children, visitorInfo, trustInfo, setTrustInfo }) => {
  const userData = useUserData();
  const { obRouteContext } = useAppContext();
  const basePath = obRouteContext?.linkProps?.basePath;

  const { hasTemporaryAccess, hasTemporaryAccessExpired } = useMemo(() => {
    const temporaryAccess = userData?.temporaryAccess;

    if (temporaryAccess?.expiry) {
      return {
        hasTemporaryAccess: true,
        hasTemporaryAccessExpired: createDate(temporaryAccess?.expiry).isBefore(createDate()),
      };
    }

    return {
      hasTemporaryAccess: false,
      hasTemporaryAccessExpired: false,
    };
  }, [userData]);

  const {
    data: { visitorId, requestId } = {},
    isLoading: isFingerprintLoading,
    error: fingerprintError,
  } = visitorInfo || {};
  const hasFingerprintFailed = !isFingerprintLoading && (!visitorId || !!fingerprintError);

  const shouldVerifyClientSide = useMemo(
    () =>
      visitorId &&
      !isFingerprintLoading &&
      !hasFingerprintFailed &&
      trustInfo?.status === RENDERING_STATUSES.UNKNOWN &&
      trustInfo?.source === VISITOR_TRUST_SOURCES.SERVER,
    [trustInfo, visitorId, hasFingerprintFailed, isFingerprintLoading],
  );

  const {
    data: clientTrustInfo,
    isLoading: isTrustInfoLoading = false,
    isError: isTrustInfoError = false,
  } = useQuery(
    queries.getTrustInfo({
      filters: {
        visitorId,
        requestId,
      },
      queryConfig: {
        enabled: shouldVerifyClientSide,
        select: response => response?.data,
      },
    }),
  );

  useEffect(() => {
    if (!isTrustInfoLoading && clientTrustInfo) {
      setTrustInfo(clientTrustInfo);
    }
  }, [clientTrustInfo, isTrustInfoLoading, setTrustInfo]);

  const { shouldBlockPageRendering, isVerifying } = useMemo(() => {
    if (trustInfo?.status === RENDERING_STATUSES.OVERRIDE) {
      return {
        shouldBlockPageRendering: false,
        isVerifying: false,
      };
    }

    if (hasTemporaryAccess) {
      return {
        shouldBlockPageRendering: hasTemporaryAccessExpired,
        isVerifying: false,
      };
    }

    if (hasFingerprintFailed) {
      return {
        shouldBlockPageRendering: true,
        isVerifying: false,
      };
    }

    const { source, status } = trustInfo || {};

    switch (status) {
      case RENDERING_STATUSES.ALLOW:
        return {
          shouldBlockPageRendering: false,
          isVerifying: false,
        };
      case RENDERING_STATUSES.BLOCK:
        return {
          shouldBlockPageRendering: true,
          isVerifying: false,
        };
      case RENDERING_STATUSES.UNKNOWN:
      default:
        return {
          shouldBlockPageRendering: true,
          isVerifying: isFingerprintLoading || (!isTrustInfoError && source === VISITOR_TRUST_SOURCES.SERVER),
        };
    }
  }, [
    hasFingerprintFailed,
    trustInfo,
    hasTemporaryAccess,
    hasTemporaryAccessExpired,
    isTrustInfoError,
    isFingerprintLoading,
  ]);

  if (!shouldBlockPageRendering || basePath === ROUTE_RESERVED_KEYWORDS.register) {
    return children;
  }

  return (
    <SuspiciousUser
      visitorInfo={visitorInfo}
      trustInfo={trustInfo}
      isVerifying={isVerifying}
      isFingerprintLoading={isFingerprintLoading}
      hasTemporaryAccess={hasTemporaryAccess}
      hasTemporaryAccessExpired={hasTemporaryAccessExpired}
      hasFingerprintFailed={hasFingerprintFailed}
    />
  );
};

export default SuspiciousUserCheck;
